/**
 * marketplace_purchases.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin — Marketplace Purchases (bot & signal subscription monitor)
 * Route suggestion: /admin/marketplace-purchases
 *
 * Add to NAV_SECTIONS in your shared sidebar config, e.g. under "Trading":
 *   { to: "/admin/marketplace-purchases", label: "Purchases", icon: ShoppingCart }
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  ShoppingCart, X, Check, ChevronLeft, ChevronRight, Search, RefreshCw,
  Users, Shield, LogOut, LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag, Menu,
  AlertCircle, CheckCircle2, XCircle, TrendingUp, TrendingDown, MapPin,
  CreditCard, Bot, Radio, Trash2, Ban, PauseCircle, PlayCircle, Eye,
  MoreVertical, DollarSign, Calendar, ChevronDown, AlertTriangle,
} from "lucide-react";

// ─── Design-system styles (mirrors subscription_plans.jsx) ──────────────────
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg-base:#06060f;--bg-surface:#0a0a16;--bg-elevated:#0e0e1c;--bg-card:#111122;--bg-hover:#14142a;
  --border:rgba(255,255,255,0.055);--border-hi:rgba(255,255,255,0.11);--border-glow:rgba(99,102,241,0.3);
  --accent:#6366f1;--accent-light:#818cf8;--accent-dim:rgba(99,102,241,0.13);--accent-glow:rgba(99,102,241,0.22);
  --green:#34d399;--green-dim:rgba(52,211,153,0.13);
  --amber:#fbbf24;--amber-dim:rgba(251,191,36,0.13);
  --rose:#fb7185;--rose-dim:rgba(251,113,133,0.13);
  --cyan:#22d3ee;--cyan-dim:rgba(34,211,238,0.13);
  --purple:#a78bfa;--purple-dim:rgba(167,139,250,0.13);
  --orange:#fb923c;--orange-dim:rgba(251,146,60,0.13);
  --blue:#60a5fa;--blue-dim:rgba(96,165,250,0.13);
  --text-primary:#eeeeff;--text-secondary:#7878a0;--text-muted:#3e3e58;
  --font-sans:'Inter',system-ui,sans-serif;--font-mono:'JetBrains Mono',monospace;
  --sidebar-w:232px;--topbar-h:60px;
  --radius-sm:8px;--radius-md:14px;--radius-lg:18px;--radius-xl:22px;
}
html,body,#root{height:100%;min-height:100%;background:var(--bg-base);font-family:var(--font-sans);color-scheme:dark;scroll-behavior:smooth;-webkit-text-size-adjust:100%;overscroll-behavior-y:none}
html{scroll-behavior:smooth}
body{overflow:hidden}

.ad-shell{display:flex;height:100vh;height:100dvh;min-height:100vh;min-height:100dvh;max-height:100dvh;overflow:hidden;background:var(--bg-base);position:relative}
.ad-shell::before{content:'';position:fixed;inset:0;pointer-events:none;z-index:0;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");opacity:.018}

.ad-overlay{display:none;position:fixed;inset:0;z-index:40;background:rgba(0,0,0,.6);backdrop-filter:blur(2px);opacity:0;transition:opacity .28s ease;pointer-events:none}
.ad-overlay.open{display:block;opacity:1;pointer-events:auto}

.ad-sidebar{width:var(--sidebar-w);flex-shrink:0;display:flex;flex-direction:column;height:100%;overflow:hidden;background:var(--bg-surface);border-right:1px solid var(--border);position:relative;z-index:50;transition:transform .3s cubic-bezier(.4,0,.2,1);will-change:transform}
.ad-sidebar::after{content:'';position:absolute;top:0;right:0;bottom:0;width:1px;background:linear-gradient(to bottom,transparent 0%,var(--accent-glow) 35%,rgba(99,102,241,.08) 70%,transparent 100%);pointer-events:none}
.ad-logo{display:flex;align-items:center;gap:11px;padding:0 18px;height:var(--topbar-h);border-bottom:1px solid var(--border);flex-shrink:0}
.ad-logo-close{display:none;margin-left:auto;width:28px;height:28px;border-radius:7px;background:none;border:none;cursor:pointer;align-items:center;justify-content:center;color:var(--text-muted);transition:all .15s;flex-shrink:0}
.ad-logo-close:hover{background:var(--rose-dim);color:var(--rose)}
.ad-logo-icon{width:32px;height:32px;border-radius:10px;background:linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a78bfa 100%);display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 0 0 1px rgba(99,102,241,.4),0 4px 16px rgba(99,102,241,.35),0 0 32px rgba(99,102,241,.15)}
.ad-logo-text{line-height:1}
.ad-logo-text strong{font-size:13.5px;font-weight:800;background:linear-gradient(to right,#eeeeff,#a5b4fc);-webkit-background-clip:text;-webkit-text-fill-color:transparent;letter-spacing:-.4px}
.ad-logo-text span{font-size:9.5px;color:var(--text-muted);display:block;margin-top:3px;letter-spacing:.8px;text-transform:uppercase;font-weight:500}
.ad-nav{flex:1;min-height:0;overflow-y:auto;padding:18px 10px;display:flex;flex-direction:column;gap:22px;-webkit-overflow-scrolling:touch;scroll-behavior:smooth}
.ad-nav::-webkit-scrollbar{width:3px}
.ad-nav::-webkit-scrollbar-track{background:transparent}
.ad-nav::-webkit-scrollbar-thumb{background:rgba(99,102,241,.18);border-radius:99px}
.ad-nav-section-label{font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-muted);padding:0 10px;margin-bottom:5px}
.ad-nav-items{display:flex;flex-direction:column;gap:1px}
.ad-nav-link{display:flex;align-items:center;gap:9px;padding:9px 10px;border-radius:var(--radius-sm);font-size:12.5px;font-weight:500;color:var(--text-secondary);text-decoration:none;transition:all .18s ease;position:relative;-webkit-tap-highlight-color:transparent;touch-action:manipulation}
.ad-nav-link:hover{background:var(--bg-hover);color:var(--text-primary)}
.ad-nav-link.active{background:linear-gradient(135deg,rgba(99,102,241,.18) 0%,rgba(139,92,246,.10) 100%);color:var(--accent-light);font-weight:600;box-shadow:inset 0 0 0 1px rgba(99,102,241,.15)}
.ad-nav-link.active::before{content:'';position:absolute;left:0;top:18%;bottom:18%;width:2.5px;border-radius:99px;background:linear-gradient(to bottom,var(--accent),var(--purple));box-shadow:0 0 10px var(--accent-glow)}
.ad-nav-link svg{flex-shrink:0;opacity:.65;transition:opacity .15s}
.ad-nav-link:hover svg,.ad-nav-link.active svg{opacity:1}
.ad-nav-chevron{margin-left:auto;opacity:.35}
.ad-user-card{flex-shrink:0;border-top:1px solid var(--border);padding:12px 10px;background:linear-gradient(to top,rgba(99,102,241,.04) 0%,transparent 100%)}
.ad-user-inner{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:var(--radius-sm);transition:background .15s}
.ad-user-inner:hover{background:var(--bg-hover)}
.ad-user-avatar{width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,var(--accent-dim),rgba(139,92,246,.2));border:1.5px solid rgba(99,102,241,.35);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:var(--accent-light);flex-shrink:0}
.ad-user-info{flex:1;min-width:0}
.ad-user-info strong{font-size:12px;font-weight:600;color:var(--text-primary);display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ad-user-info span{font-size:10px;color:var(--text-muted);display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px}
.ad-logout-btn{width:28px;height:28px;border-radius:7px;background:none;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--text-muted);transition:all .15s;-webkit-tap-highlight-color:transparent}
.ad-logout-btn:hover{background:var(--rose-dim);color:var(--rose)}

.ad-page{flex:1;display:flex;flex-direction:column;overflow:hidden;position:relative;z-index:1;min-width:0;min-height:0;height:100%}
.ad-topbar{flex-shrink:0;height:var(--topbar-h);background:rgba(10,10,22,.92);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;padding:0 24px;position:relative;gap:10px}
.ad-topbar::after{content:'';position:absolute;bottom:-1px;left:0;right:0;height:1px;background:linear-gradient(to right,transparent 0%,var(--accent-glow) 50%,transparent 100%)}
.ad-topbar-left{display:flex;align-items:center;gap:12px;min-width:0;flex:1}
.ad-topbar-title{font-size:15px;font-weight:700;color:var(--text-primary);letter-spacing:-.4px;white-space:nowrap}
.ad-topbar-sub{font-size:11px;color:var(--text-muted);margin-top:1px}
.ad-topbar-right{display:flex;align-items:center;gap:8px;flex-shrink:0}
.ad-hamburger{display:none;width:36px;height:36px;border-radius:var(--radius-sm);background:var(--bg-card);border:1px solid var(--border);cursor:pointer;align-items:center;justify-content:center;color:var(--text-secondary);transition:all .15s;flex-shrink:0;-webkit-tap-highlight-color:transparent}
.ad-hamburger:hover{background:var(--accent-dim);color:var(--accent-light);border-color:rgba(99,102,241,.25)}

.ad-main{flex:1;min-height:0;overflow-y:auto;overflow-x:hidden;padding:26px 28px;display:flex;flex-direction:column;gap:24px;-webkit-overflow-scrolling:touch;scroll-behavior:smooth;overscroll-behavior-y:contain}
.ad-main::-webkit-scrollbar{width:4px}
.ad-main::-webkit-scrollbar-track{background:transparent}
.ad-main::-webkit-scrollbar-thumb{background:rgba(99,102,241,.25);border-radius:99px}

.sp-page-header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap}
.sp-page-header-text h1{font-size:22px;font-weight:800;color:var(--text-primary);letter-spacing:-.6px}
.sp-page-header-text p{font-size:12px;color:var(--text-muted);margin-top:4px}

.sp-kpi-row{display:grid;grid-template-columns:repeat(6,1fr);gap:14px}
.sp-kpi{background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-md);padding:18px 20px;display:flex;align-items:center;gap:14px;transition:border-color .2s,box-shadow .2s,transform .18s;position:relative;overflow:hidden}
.sp-kpi:hover{border-color:var(--border-hi);box-shadow:0 4px 20px rgba(0,0,0,.3);transform:translateY(-1px)}
.sp-kpi::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--kpi-bar,transparent)}
.sp-kpi-icon{width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.sp-kpi-body{min-width:0;flex:1}
.sp-kpi-val{font-size:26px;font-weight:700;color:var(--text-primary);font-family:var(--font-mono);letter-spacing:-.9px;line-height:1}
.sp-kpi-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.9px;color:var(--text-muted);margin-top:4px}
.sp-kpi-sub{font-size:10.5px;font-weight:600;margin-top:5px;display:flex;align-items:center;gap:3px}
.sp-kpi-sub.up{color:var(--green)} .sp-kpi-sub.down{color:var(--rose)} .sp-kpi-sub.neu{color:var(--text-muted)}

.sp-panel{background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-md);overflow:hidden;display:flex;flex-direction:column;transition:border-color .2s}
.sp-panel:hover{border-color:var(--border-hi)}
.sp-panel-header{padding:16px 20px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;background:linear-gradient(to bottom,rgba(255,255,255,.02),transparent);flex-shrink:0}
.sp-panel-title{font-size:13px;font-weight:700;color:var(--text-primary);letter-spacing:-.2px}
.sp-panel-sub{font-size:11px;color:var(--text-muted);margin-top:2px}

.sp-toolbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.sp-search-wrap{position:relative}
.sp-search-wrap svg{position:absolute;left:11px;top:50%;transform:translateY(-50%);color:var(--text-muted);pointer-events:none}
.sp-search{background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:var(--radius-sm);padding:7px 12px 7px 33px;font-size:12px;color:var(--text-primary);font-family:var(--font-sans);outline:none;width:220px;transition:border-color .18s,box-shadow .18s}
.sp-search::placeholder{color:var(--text-muted)}
.sp-search:focus{border-color:rgba(99,102,241,.4);box-shadow:0 0 0 3px rgba(99,102,241,.08)}
.sp-select{background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:var(--radius-sm);padding:7px 30px 7px 11px;font-size:12px;color:var(--text-primary);font-family:var(--font-sans);outline:none;cursor:pointer;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237878a0' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 9px center;transition:border-color .18s;min-width:130px}
.sp-select:focus{border-color:rgba(99,102,241,.4);box-shadow:0 0 0 3px rgba(99,102,241,.08)}
.sp-select-sm{padding:5px 26px 5px 9px;font-size:11px;min-width:0}

.sp-table-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch;flex:1}
table.sp-table{width:100%;border-collapse:collapse;font-size:12.5px}
table.sp-table thead th{text-align:left;font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;color:var(--text-muted);padding:11px 16px;border-bottom:1px solid var(--border);white-space:nowrap;background:rgba(255,255,255,.015)}
table.sp-table tbody tr{border-bottom:1px solid var(--border);transition:background .14s;cursor:pointer;position:relative}
table.sp-table tbody tr:hover{background:var(--bg-hover)}
table.sp-table tbody tr:last-child{border-bottom:none}
table.sp-table tbody tr.pending-row{background:rgba(96,165,250,.05)}
table.sp-table tbody tr.pending-row::before{content:'';position:absolute;left:0;top:0;bottom:0;width:2.5px;background:var(--blue)}
.sp-approve-btn{background:var(--green-dim);border:1px solid rgba(52,211,153,.3);color:var(--green);width:26px;height:26px;border-radius:6px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0}
.sp-approve-btn:hover{background:rgba(52,211,153,.25);box-shadow:0 0 10px rgba(52,211,153,.25)}
table.sp-table td{padding:12px 16px;vertical-align:middle;color:var(--text-secondary);white-space:nowrap}

.sp-avatar-cell{display:flex;align-items:center;gap:10px}
.sp-avatar{width:30px;height:30px;border-radius:50%;background:linear-gradient(135deg,var(--accent-dim),rgba(139,92,246,.2));border:1.5px solid rgba(99,102,241,.3);display:flex;align-items:center;justify-content:center;font-size:10.5px;font-weight:700;color:var(--accent-light);flex-shrink:0}
.sp-cell-name{font-size:12.5px;font-weight:600;color:var(--text-primary)}
.sp-cell-email{font-size:10.5px;color:var(--text-muted);margin-top:1px}
.sp-cell-mono{font-family:var(--font-mono);font-size:11.5px;color:var(--text-secondary)}
.sp-cell-date{font-size:11.5px;color:var(--text-secondary)}

.sp-item-pill{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:600;color:var(--text-primary)}
.sp-item-icon{width:22px;height:22px;border-radius:7px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.sp-type-chip{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:99px;font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;border:1px solid}
.sp-type-chip.bot{background:var(--purple-dim);color:var(--purple);border-color:rgba(167,139,250,.25)}
.sp-type-chip.signal{background:var(--cyan-dim);color:var(--cyan);border-color:rgba(34,211,238,.25)}

.sp-chip{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:99px;font-size:10.5px;font-weight:700;text-transform:capitalize}
.sp-chip.pending{background:var(--blue-dim);color:var(--blue)}
.sp-chip.active{background:var(--green-dim);color:var(--green)}
.sp-chip.paused{background:var(--amber-dim);color:var(--amber)}
.sp-chip.suspended{background:var(--orange-dim);color:var(--orange)}
.sp-chip.cancelled{background:var(--rose-dim);color:var(--rose)}
.sp-chip.expired{background:rgba(120,120,160,.13);color:var(--text-muted)}
.sp-dot-pulse{width:6px;height:6px;border-radius:50%;background:var(--green);box-shadow:0 0 0 0 rgba(52,211,153,.6);animation:sp-pulse 1.8s infinite}
@keyframes sp-pulse{0%{box-shadow:0 0 0 0 rgba(52,211,153,.5)}70%{box-shadow:0 0 0 6px rgba(52,211,153,0)}100%{box-shadow:0 0 0 0 rgba(52,211,153,0)}}

.sp-pnl{font-family:var(--font-mono);font-weight:700;font-size:12px}
.sp-pnl.pos{color:var(--green)} .sp-pnl.neg{color:var(--rose)} .sp-pnl.zero{color:var(--text-muted)}

.sp-row-actions{display:flex;align-items:center;gap:6px}
.sp-status-select{background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:6px;padding:5px 22px 5px 8px;font-size:10.5px;font-weight:600;color:var(--text-primary);font-family:var(--font-sans);outline:none;cursor:pointer;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%237878a0' stroke-width='3'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 7px center}
.sp-status-select:focus{border-color:rgba(99,102,241,.4)}
.sp-icon-btn{width:26px;height:26px;border-radius:6px;background:var(--bg-elevated);border:1px solid var(--border-hi);cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--text-muted);transition:all .15s;flex-shrink:0}
.sp-icon-btn:hover{background:var(--rose-dim);color:var(--rose);border-color:rgba(251,113,133,.3)}

.sp-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;padding:56px 20px;color:var(--text-muted);font-size:12px;text-align:center}
.sp-empty svg{opacity:.3;margin-bottom:6px}

.sp-btn{display:inline-flex;align-items:center;gap:7px;padding:8px 16px;border-radius:var(--radius-sm);font-size:12px;font-weight:600;cursor:pointer;transition:all .18s;border:none;font-family:var(--font-sans);white-space:nowrap;-webkit-tap-highlight-color:transparent;text-decoration:none}
.sp-btn-primary{background:linear-gradient(135deg,var(--accent) 0%,#7c3aed 100%);color:#fff;box-shadow:0 2px 12px rgba(99,102,241,.35)}
.sp-btn-primary:hover{box-shadow:0 4px 20px rgba(99,102,241,.5);transform:translateY(-1px)}
.sp-btn-ghost{background:var(--bg-elevated);border:1px solid var(--border-hi);color:var(--text-secondary)}
.sp-btn-ghost:hover{background:var(--bg-hover);color:var(--text-primary);border-color:var(--border-glow)}
.sp-btn-danger{background:var(--rose-dim);border:1px solid rgba(251,113,133,.22);color:var(--rose)}
.sp-btn-danger:hover{background:rgba(251,113,133,.2);box-shadow:0 0 12px rgba(251,113,133,.2)}
.sp-btn-sm{padding:5px 11px;font-size:11px}
.sp-btn:disabled{opacity:.4;cursor:not-allowed;transform:none!important}

.sp-skel{height:10px;border-radius:5px;background:linear-gradient(90deg,var(--bg-hover) 25%,rgba(99,102,241,.05) 50%,var(--bg-hover) 75%);background-size:200% 100%;animation:sp-shimmer 1.8s infinite}
@keyframes sp-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}

.sp-toast-wrap{position:fixed;bottom:24px;right:24px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none}
.sp-toast{display:flex;align-items:center;gap:10px;padding:12px 18px;border-radius:var(--radius-md);font-size:12.5px;font-weight:500;color:var(--text-primary);backdrop-filter:blur(12px);border:1px solid;animation:sp-toast-in .3s ease;pointer-events:auto;min-width:240px;max-width:360px;box-shadow:0 8px 32px rgba(0,0,0,.4)}
.sp-toast.success{background:rgba(14,14,28,.96);border-color:rgba(52,211,153,.25)}
.sp-toast.error{background:rgba(14,14,28,.96);border-color:rgba(251,113,133,.25)}
.sp-toast.info{background:rgba(14,14,28,.96);border-color:rgba(99,102,241,.25)}
@keyframes sp-toast-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}

.sp-confirm-bg{position:fixed;inset:0;z-index:300;background:rgba(0,0,0,.72);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px}
.sp-confirm{background:var(--bg-surface);border:1px solid var(--border-hi);border-radius:var(--radius-lg);padding:28px;max-width:380px;width:100%;animation:sp-modal-up .22s cubic-bezier(.34,1.56,.64,1)}
.sp-confirm-icon{width:44px;height:44px;border-radius:14px;display:flex;align-items:center;justify-content:center;margin-bottom:16px}
.sp-confirm h3{font-size:15px;font-weight:700;color:var(--text-primary);margin-bottom:8px}
.sp-confirm p{font-size:12.5px;color:var(--text-muted);line-height:1.55}
.sp-confirm-actions{display:flex;gap:10px;margin-top:22px;justify-content:flex-end}
@keyframes sp-modal-up{from{transform:translateY(16px) scale(.98);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}

.sp-drawer-bg{position:fixed;inset:0;z-index:150;background:rgba(0,0,0,.55);backdrop-filter:blur(3px);animation:sp-fade .22s ease}
@keyframes sp-fade{from{opacity:0}to{opacity:1}}
.sp-drawer{position:fixed;top:0;right:0;bottom:0;width:100%;max-width:420px;background:var(--bg-surface);border-left:1px solid var(--border-hi);display:flex;flex-direction:column;z-index:151;animation:sp-drawer-in .28s cubic-bezier(.4,0,.2,1);overflow:hidden}
@keyframes sp-drawer-in{from{transform:translateX(100%)}to{transform:translateX(0)}}
.sp-drawer-header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px;border-bottom:1px solid var(--border);flex-shrink:0}
.sp-drawer-title{font-size:14px;font-weight:700;color:var(--text-primary)}
.sp-drawer-body{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:20px;display:flex;flex-direction:column;gap:20px}
.sp-drawer-section{display:flex;flex-direction:column;gap:10px}
.sp-drawer-section-title{font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:var(--text-muted)}
.sp-drawer-row{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:var(--bg-card);border-radius:var(--radius-sm);border:1px solid var(--border)}
.sp-drawer-row-label{font-size:11.5px;color:var(--text-muted)}
.sp-drawer-row-val{font-size:12px;font-weight:600;color:var(--text-primary);text-align:right}
.sp-drawer-actions{display:flex;flex-direction:column;gap:8px;padding:0 20px 20px;flex-shrink:0}

.sp-pagination{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-top:1px solid var(--border);flex-wrap:wrap;gap:10px}
.sp-pagination-info{font-size:11px;color:var(--text-muted)}
.sp-pagination-btns{display:flex;align-items:center;gap:4px}
.sp-page-btn{width:26px;height:26px;border-radius:6px;background:var(--bg-elevated);border:1px solid var(--border-hi);color:var(--text-secondary);font-size:11px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s}
.sp-page-btn:hover{background:var(--bg-hover);color:var(--text-primary)}
.sp-page-btn.active{background:var(--accent);color:#fff;border-color:var(--accent)}
.sp-page-btn:disabled{opacity:.35;cursor:not-allowed}

@media(min-width:1400px){:root{--sidebar-w:252px}.ad-main{padding:30px 36px}.ad-topbar{padding:0 36px}}
@media(max-width:1500px){.sp-kpi-row{grid-template-columns:repeat(3,1fr)}}
@media(max-width:1023px){:root{--sidebar-w:220px}.ad-main{padding:20px;gap:20px}.ad-topbar{padding:0 16px}}
@media(max-width:899px){.sp-kpi-row{grid-template-columns:repeat(2,1fr)}}
@media(max-width:767px){
  :root{--topbar-h:56px}
  .ad-hamburger{display:flex}
  .ad-logo-close{display:flex}
  .ad-sidebar{position:fixed;top:0;left:0;bottom:0;height:100%;height:100dvh;overflow:hidden;transform:translateX(-100%);box-shadow:4px 0 32px rgba(0,0,0,.6)}
  .ad-sidebar.open{transform:translateX(0)}
  .ad-topbar{padding:0 14px}
  .ad-topbar-sub{display:none}
  .ad-main{padding:14px;gap:16px}
  .sp-kpi-row{grid-template-columns:repeat(2,1fr);gap:10px}
  .sp-panel-header{flex-direction:column;align-items:stretch}
  .sp-toolbar{flex-direction:column;align-items:stretch}
  .sp-search{width:100%}
  .sp-select{width:100%}
  .sp-page-header{flex-direction:column;gap:12px}
  .sp-drawer{max-width:100%}
  .sp-toast-wrap{left:14px;right:14px;bottom:14px}
}
@media(max-width:479px){
  .sp-kpi-row{grid-template-columns:1fr 1fr;gap:8px}
  .sp-kpi{padding:14px 12px;gap:10px}
  .sp-kpi-val{font-size:22px}
}
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Nav config (mirror of your shared sidebar — trim/merge with your real one) ─
const NAV_SECTIONS = [
  { label: "Overview",        items: [{ to: "/admin",               label: "Dashboard",      icon: LayoutDashboard, end: true }] },
  { label: "Users & Finance", items: [
    { to: "/admin/users",        label: "Users",        icon: Users        },
    { to: "/admin/wallets",      label: "Wallets",      icon: Wallet       },
    { to: "/admin/transactions", label: "Transactions", icon: Receipt      },
  ]},
  { label: "Trading", items: [
    { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
    { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
    { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
    { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
    { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    { to: "/admin/marketplace-purchases", label: "Purchases", icon: ShoppingCart },
  ]},
  { label: "Communication", items: [
    { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
    { to: "/admin/notifications", label: "Notifications", icon: Bell           },
  ]},
  { label: "Settings", items: [
    { to: "/admin/subscriptions",     label: "Subscriptions",     icon: CreditCard },
    { to: "/admin/payment-addresses", label: "Payment Addresses", icon: MapPin     },
  ]},
];

function SidebarLink({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick}
      className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────────
const fmt$ = (v) => v == null ? "—"
  : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);

const fmtDate = (s) => !s ? "—"
  : new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const initials = (a, b) => `${a?.[0] ?? ""}${b?.[0] ?? ""}`.toUpperCase() || "?";

const PAGE_SIZE = 15;

// Allowed admin-settable statuses. varchar(15) column — keep values short.
// "pending" = awaiting admin approval before the bot/signal is provisioned.
const STATUS_OPTIONS = ["pending", "active", "paused", "suspended", "cancelled", "expired"];

const TYPE_THEME = {
  bot:    { icon: Bot,   bg: "var(--purple-dim)", color: "var(--purple)" },
  signal: { icon: Radio, bg: "var(--cyan-dim)",   color: "var(--cyan)"   },
};
const getTypeTheme = (t) => TYPE_THEME[t?.toLowerCase()] ?? TYPE_THEME.bot;

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toasts({ list }) {
  return (
    <div className="sp-toast-wrap">
      {list.map((t) => (
        <div key={t.id} className={`sp-toast ${t.type}`}>
          {t.type === "success" && <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} />}
          {t.type === "error"   && <XCircle      size={14} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          {t.type === "info"    && <AlertCircle  size={14} style={{ color: "var(--accent-light)", flexShrink: 0 }} />}
          <span style={{ flex: 1 }}>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Confirm delete dialog ────────────────────────────────────────────────────
function ConfirmDeletePurchase({ purchase, onCancel, onConfirm, busy }) {
  return (
    <div className="sp-confirm-bg">
      <div className="sp-confirm">
        <div className="sp-confirm-icon" style={{ background: "var(--rose-dim)", border: "1px solid rgba(251,113,133,.2)" }}>
          <Trash2 size={18} style={{ color: "var(--rose)" }} />
        </div>
        <h3>Delete this purchase?</h3>
        <p>
          This permanently removes {purchase.user?.first_name ?? "this user"}'s subscription to{" "}
          <strong style={{ color: "var(--text-secondary)" }}>{purchase.item?.name ?? "this item"}</strong>.
          This cannot be undone — consider setting status to "cancelled" instead if you just want to revoke access.
        </p>
        <div className="sp-confirm-actions">
          <button className="sp-btn sp-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="sp-btn sp-btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Deleting…" : "Delete purchase"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail drawer ─────────────────────────────────────────────────────────────
function PurchaseDrawer({ purchase, onClose, onStatusChange, onDelete, actionBusy }) {
  const theme = getTypeTheme(purchase.item?.type ?? purchase.item_type);
  const TypeIcon = theme.icon;
  return (
    <>
      <div className="sp-drawer-bg" onClick={onClose} />
      <div className="sp-drawer">
        <div className="sp-drawer-header">
          <span className="sp-drawer-title">Purchase details</span>
          <button className="sp-btn sp-btn-ghost sp-btn-icon" onClick={onClose} style={{ width: 30, height: 30, padding: 0, justifyContent: "center" }}>
            <X size={14} />
          </button>
        </div>
        <div className="sp-drawer-body">

          <div className="sp-drawer-section">
            <div className="sp-drawer-section-title">Subscriber</div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">Name</span>
              <span className="sp-drawer-row-val">{purchase.user?.first_name ?? "—"} {purchase.user?.last_name ?? ""}</span>
            </div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">Email</span>
              <span className="sp-drawer-row-val">{purchase.user?.email ?? "—"}</span>
            </div>
          </div>

          <div className="sp-drawer-section">
            <div className="sp-drawer-section-title">Item</div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">Name</span>
              <span className="sp-drawer-row-val">
                <span className="sp-item-pill">
                  <span className="sp-item-icon" style={{ background: theme.bg }}><TypeIcon size={11} style={{ color: theme.color }} /></span>
                  {purchase.item?.name ?? "—"}
                </span>
              </span>
            </div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">Type</span>
              <span className={`sp-type-chip ${(purchase.item?.type ?? purchase.item_type ?? "").toLowerCase()}`}>
                {purchase.item?.type ?? purchase.item_type ?? "—"}
              </span>
            </div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">Monthly price</span>
              <span className="sp-drawer-row-val">{fmt$(purchase.item?.price_monthly)}</span>
            </div>
          </div>

          <div className="sp-drawer-section">
            <div className="sp-drawer-section-title">Subscription</div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">Status</span>
              <span className={`sp-chip ${purchase.status}`}>{purchase.status}</span>
            </div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">30D PnL</span>
              <span className={`sp-pnl ${purchase.pnl_30d > 0 ? "pos" : purchase.pnl_30d < 0 ? "neg" : "zero"}`}>
                {purchase.pnl_30d > 0 ? "+" : ""}{fmt$(purchase.pnl_30d)}
              </span>
            </div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">Started</span>
              <span className="sp-drawer-row-val">{fmtDate(purchase.started_at)}</span>
            </div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">Renews</span>
              <span className="sp-drawer-row-val">{fmtDate(purchase.renews_at)}</span>
            </div>
            <div className="sp-drawer-row">
              <span className="sp-drawer-row-label">Cancelled</span>
              <span className="sp-drawer-row-val" style={{ color: purchase.cancelled_at ? "var(--rose)" : "var(--text-primary)" }}>
                {fmtDate(purchase.cancelled_at)}
              </span>
            </div>
          </div>

        </div>

        <div className="sp-drawer-actions">
          {purchase.status === "pending" && (
            <>
              <button
                className="sp-btn sp-btn-primary"
                style={{ width: "100%", justifyContent: "center", background: "linear-gradient(135deg,var(--green) 0%,#059669 100%)", boxShadow: "0 2px 12px rgba(52,211,153,.35)" }}
                disabled={actionBusy}
                onClick={() => onStatusChange(purchase, "active")}
              >
                <Check size={13} /> {actionBusy ? "Approving…" : "Approve purchase"}
              </button>
              <button
                className="sp-btn sp-btn-danger"
                style={{ width: "100%", justifyContent: "center" }}
                disabled={actionBusy}
                onClick={() => onStatusChange(purchase, "cancelled")}
              >
                <Ban size={13} /> Reject purchase
              </button>
            </>
          )}
          <label className="sp-drawer-section-title" style={{ marginBottom: -2 }}>Change status</label>
          <select
            className="sp-input"
            style={{ cursor: "pointer" }}
            value={purchase.status}
            disabled={actionBusy}
            onChange={(e) => onStatusChange(purchase, e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
          </select>
          <button
            className="sp-btn sp-btn-danger"
            style={{ width: "100%", justifyContent: "center" }}
            disabled={actionBusy}
            onClick={() => onDelete(purchase)}
          >
            <Trash2 size={13} /> Delete purchase
          </button>
          <button className="sp-btn sp-btn-ghost" style={{ width: "100%", justifyContent: "center" }} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────
export default function AdminMarketplacePurchases() {
  const navigate = useNavigate();

  // auth
  const [session,    setSession]    = useState(null);
  const [sesLoading, setSesLoading] = useState(true);

  // mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  // purchases
  const [rows,        setRows]        = useState([]);
  const [rowsLoading,  setRowsLoading] = useState(true);
  const [total,        setTotal]       = useState(0);
  const [page,         setPage]        = useState(1);
  const [search,       setSearch]      = useState("");
  const [fStatus,      setFStatus]     = useState("all");
  const [fType,        setFType]       = useState("all");

  // stats
  const [stats, setStats] = useState({ total: 0, pending: 0, active: 0, paused: 0, suspended: 0, cancelled: 0 });

  // drawer / actions
  const [drawerRow,   setDrawerRow]   = useState(null);
  const [actionBusy,  setActionBusy]  = useState(false);
  const [deleteRow,   setDeleteRow]   = useState(null);
  const [deleteBusy,  setDeleteBusy]  = useState(false);

  // toasts
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!s) { navigate("/admin/login", { replace: true }); return; }
      setSession(s); setSesLoading(false); loadAll();
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      if (!s) navigate("/admin/login", { replace: true });
      else setSession(s);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAll() {
    await fetchStats();
    fetchRows();
  }

  // ── Stats ─────────────────────────────────────────────────────────────────
  async function fetchStats() {
    const [
      { count: total     },
      { count: pending   },
      { count: active    },
      { count: paused    },
      { count: suspended },
      { count: cancelled },
    ] = await Promise.all([
      supabase.from("marketplace_subscriptions").select("*", { count: "exact", head: true }),
      supabase.from("marketplace_subscriptions").select("*", { count: "exact", head: true }).eq("status", "pending"),
      supabase.from("marketplace_subscriptions").select("*", { count: "exact", head: true }).eq("status", "active"),
      supabase.from("marketplace_subscriptions").select("*", { count: "exact", head: true }).eq("status", "paused"),
      supabase.from("marketplace_subscriptions").select("*", { count: "exact", head: true }).eq("status", "suspended"),
      supabase.from("marketplace_subscriptions").select("*", { count: "exact", head: true }).eq("status", "cancelled"),
    ]);
    setStats({
      total:     total     ?? 0,
      pending:   pending   ?? 0,
      active:    active    ?? 0,
      paused:    paused    ?? 0,
      suspended: suspended ?? 0,
      cancelled: cancelled ?? 0,
    });
  }

  // ── Purchases (paginated) ────────────────────────────────────────────────
  async function fetchRows(pg = page, sch = search, status = fStatus, itemType = fType) {
    setRowsLoading(true);
    const from = (pg - 1) * PAGE_SIZE;
    const to   = from + PAGE_SIZE - 1;

    let q = supabase
      .from("marketplace_subscriptions")
      .select(`
        id, status, pnl_30d, renews_at, started_at, cancelled_at, item_id, user_id, item_type,
        user:users(id, first_name, last_name, email),
        item:marketplace_items(id, name, slug, type, price_monthly)
      `, { count: "exact" })
      .order("started_at", { ascending: false })
      .range(from, to);

    if (status   !== "all") q = q.eq("status", status);
    if (itemType !== "all") q = q.eq("item_type", itemType);

    const { data, count, error } = await q;
    if (error) { setRowsLoading(false); addToast(error.message, "error"); return; }

    let merged = data ?? [];
    if (sch.trim()) {
      const q2 = sch.toLowerCase();
      merged = merged.filter((r) => {
        const name  = `${r.user?.first_name ?? ""} ${r.user?.last_name ?? ""}`.toLowerCase();
        const email = (r.user?.email ?? "").toLowerCase();
        const item  = (r.item?.name ?? "").toLowerCase();
        return name.includes(q2) || email.includes(q2) || item.includes(q2);
      });
    }

    setRows(merged);
    setTotal(count ?? 0);
    setRowsLoading(false);
  }

  // Re-fetch on filter/page change
  const prevFilters = useRef({ page, fStatus, fType });
  useEffect(() => {
    if (sesLoading) return;
    const prev = prevFilters.current;
    if (prev.page !== page || prev.fStatus !== fStatus || prev.fType !== fType) {
      fetchRows(page, search, fStatus, fType);
      prevFilters.current = { page, fStatus, fType };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fStatus, fType, sesLoading]);

  const searchDebounce = useRef(null);
  const applySearch = (v) => {
    setSearch(v);
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => { setPage(1); fetchRows(1, v, fStatus, fType); }, 320);
  };
  const applyFilter = (key, val) => {
    setPage(1);
    if (key === "status") setFStatus(val);
    if (key === "type")   setFType(val);
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  async function handleStatusChange(row, newStatus) {
    setActionBusy(true);
    const payload = { status: newStatus };
    if (newStatus === "cancelled" && !row.cancelled_at) payload.cancelled_at = new Date().toISOString();
    if (newStatus === "active") payload.cancelled_at = null;

    const { error } = await supabase.from("marketplace_subscriptions").update(payload).eq("id", row.id);
    setActionBusy(false);
    if (error) { addToast(error.message, "error"); return; }

    addToast(`Status updated to "${newStatus}"`, "success");
    setDrawerRow((d) => d && d.id === row.id ? { ...d, ...payload } : d);
    fetchRows(); fetchStats();
  }

  async function doDelete() {
    if (!deleteRow) return;
    setDeleteBusy(true);
    const { error } = await supabase.from("marketplace_subscriptions").delete().eq("id", deleteRow.id);
    setDeleteBusy(false);
    if (error) { addToast(error.message, "error"); return; }

    addToast("Purchase deleted", "success");
    setDeleteRow(null);
    setDrawerRow((d) => d && d.id === deleteRow.id ? null : d);
    fetchRows(); fetchStats();
  }

  async function handleLogout() { await supabase.auth.signOut(); }

  const adminEmail    = session?.user?.email ?? "admin@vantrexmarket.io";
  const adminInitials = adminEmail.slice(0, 2).toUpperCase();
  const totalPages    = Math.ceil(total / PAGE_SIZE);

  if (sesLoading) return (
    <>
      <StyleInjector />
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-base)", flexDirection: "column", gap: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: 11, background: "linear-gradient(135deg,#6366f1,#a78bfa)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Shield size={17} color="#fff" />
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Verifying session…</div>
      </div>
    </>
  );

  const kpis = [
    { label: "Total purchases", val: stats.total,     icon: ShoppingCart, iconBg: "var(--accent-dim)", iconColor: "var(--accent-light)", bar: "linear-gradient(to right,#6366f1,#8b5cf6)", sub: "all time", subCls: "neu" },
    { label: "Pending approval", val: stats.pending,  icon: AlertCircle,  iconBg: "var(--blue-dim)",   iconColor: "var(--blue)",          bar: "linear-gradient(to right,#60a5fa,#3b82f6)", sub: stats.pending ? "needs review" : "all clear", subCls: stats.pending ? "down" : "up", onClick: () => applyFilter("status", "pending") },
    { label: "Active",          val: stats.active,    icon: CheckCircle2, iconBg: "var(--green-dim)",  iconColor: "var(--green)",         bar: "linear-gradient(to right,#34d399,#059669)", sub: stats.total ? `${Math.round(stats.active/stats.total*100)}% of total` : "—", subCls: "up" },
    { label: "Paused",          val: stats.paused,    icon: PauseCircle,  iconBg: "var(--amber-dim)",  iconColor: "var(--amber)",         bar: "linear-gradient(to right,#fbbf24,#d97706)", sub: "temporarily halted", subCls: "neu" },
    { label: "Suspended",       val: stats.suspended, icon: AlertTriangle, iconBg: "var(--orange-dim)", iconColor: "var(--orange)",        bar: "linear-gradient(to right,#fb923c,#ea580c)", sub: "admin flagged", subCls: "down" },
    { label: "Cancelled",       val: stats.cancelled, icon: XCircle,      iconBg: "var(--rose-dim)",   iconColor: "var(--rose)",          bar: "linear-gradient(to right,#fb7185,#e11d48)", sub: stats.total ? `${Math.round(stats.cancelled/stats.total*100)}% churn` : "—", subCls: "down" },
  ];

  return (
    <>
      <StyleInjector />
      <Toasts list={toasts} />

      {drawerRow && (
        <PurchaseDrawer
          purchase={drawerRow}
          onClose={() => setDrawerRow(null)}
          onStatusChange={handleStatusChange}
          onDelete={(r) => setDeleteRow(r)}
          actionBusy={actionBusy}
        />
      )}
      {deleteRow && (
        <ConfirmDeletePurchase
          purchase={deleteRow}
          onCancel={() => setDeleteRow(null)}
          onConfirm={doDelete}
          busy={deleteBusy}
        />
      )}

      <div className="ad-shell">
        <div className={`ad-overlay${sidebarOpen ? " open" : ""}`} onClick={closeSidebar} aria-hidden="true" />

        <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
          <div className="ad-logo">
            <div className="ad-logo-icon"><Shield size={13} color="#fff" /></div>
            <div className="ad-logo-text">
              <strong>vantrexmarket</strong>
              <span>Admin panel</span>
            </div>
            <button className="ad-logo-close" onClick={closeSidebar}><X size={14} /></button>
          </div>
          <nav className="ad-nav">
            {NAV_SECTIONS.map((sec) => (
              <div key={sec.label}>
                <p className="ad-nav-section-label">{sec.label}</p>
                <div className="ad-nav-items">
                  {sec.items.map((item) => (
                    <SidebarLink key={item.to} {...item} onClick={closeSidebar} />
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="ad-user-card">
            <div className="ad-user-inner">
              <div className="ad-user-avatar">{adminInitials}</div>
              <div className="ad-user-info">
                <strong>Super Admin</strong>
                <span>{adminEmail}</span>
              </div>
              <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
                <LogOut size={13} />
              </button>
            </div>
          </div>
        </aside>

        <div className="ad-page">
          <header className="ad-topbar">
            <div className="ad-topbar-left">
              <button className="ad-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
                <Menu size={16} />
              </button>
              <div>
                <div className="ad-topbar-title">Marketplace Purchases</div>
                <div className="ad-topbar-sub">Monitor bot &amp; signal subscriptions</div>
              </div>
            </div>
            <div className="ad-topbar-right">
              <button className="sp-btn sp-btn-ghost sp-btn-sm" onClick={loadAll} title="Refresh">
                <RefreshCw size={12} /> <span style={{ fontSize: 11 }}>Refresh</span>
              </button>
            </div>
          </header>

          <main className="ad-main">

            <div className="sp-page-header">
              <div className="sp-page-header-text">
                <h1>Marketplace Purchases</h1>
                <p>Approve or reject pending purchases, monitor 30-day PnL, and manage access to bots and signals.</p>
              </div>
            </div>

            <div className="sp-kpi-row">
              {kpis.map(({ label, val, icon: Icon, iconBg, iconColor, bar, sub, subCls, onClick }) => (
                <div key={label} className="sp-kpi" style={{ "--kpi-bar": bar, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
                  <div className="sp-kpi-icon" style={{ background: iconBg }}>
                    <Icon size={17} style={{ color: iconColor }} />
                  </div>
                  <div className="sp-kpi-body">
                    <div className="sp-kpi-val">{val.toLocaleString()}</div>
                    <div className="sp-kpi-label">{label}</div>
                    <div className={`sp-kpi-sub ${subCls}`}>
                      {subCls === "up"   && <TrendingUp   size={10} />}
                      {subCls === "down" && <TrendingDown size={10} />}
                      {sub}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="sp-panel">
              <div className="sp-panel-header">
                <div>
                  <div className="sp-panel-title">Purchases</div>
                  <div className="sp-panel-sub">
                    {total.toLocaleString()} total purchase{total !== 1 ? "s" : ""}
                    {fStatus !== "all" || fType !== "all" || search ? " (filtered)" : ""}
                  </div>
                </div>
                <div className="sp-toolbar">
                  <div className="sp-search-wrap">
                    <Search size={12} />
                    <input
                      className="sp-search"
                      placeholder="Search name, email, item…"
                      value={search}
                      onChange={(e) => applySearch(e.target.value)}
                    />
                  </div>
                  <select className="sp-select" value={fType} onChange={(e) => applyFilter("type", e.target.value)}>
                    <option value="all">All types</option>
                    <option value="bot">Bots</option>
                    <option value="signal">Signals</option>
                  </select>
                  <select className="sp-select" value={fStatus} onChange={(e) => applyFilter("status", e.target.value)}>
                    <option value="all">All statuses</option>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
              </div>

              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>Subscriber</th>
                      <th>Item</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>30D PnL</th>
                      <th>Started</th>
                      <th>Renews</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsLoading
                      ? Array.from({ length: 8 }).map((_, i) => (
                          <tr key={i}>
                            {Array.from({ length: 8 }).map((_, j) => (
                              <td key={j} style={{ padding: "13px 16px" }}>
                                <div className="sp-skel" style={{ width: `${35 + (j * 17) % 50}%` }} />
                              </td>
                            ))}
                          </tr>
                        ))
                      : rows.length === 0
                      ? (
                          <tr>
                            <td colSpan={8}>
                              <div className="sp-empty">
                                <ShoppingCart size={24} />
                                <div style={{ fontWeight: 600 }}>No purchases found</div>
                                <div>Try adjusting your filters or search query.</div>
                              </div>
                            </td>
                          </tr>
                        )
                      : rows.map((r) => {
                          const type  = (r.item?.type ?? r.item_type ?? "bot").toLowerCase();
                          const theme = getTypeTheme(type);
                          const TypeIcon = theme.icon;
                          const chipCls = STATUS_OPTIONS.includes(r.status) ? r.status : "active";
                          return (
                            <tr key={r.id} className={r.status === "pending" ? "pending-row" : ""} onClick={() => setDrawerRow(r)} title="Click to view details">
                              <td>
                                <div className="sp-avatar-cell">
                                  <div className="sp-avatar">{initials(r.user?.first_name, r.user?.last_name)}</div>
                                  <div>
                                    <div className="sp-cell-name">{r.user?.first_name ?? "—"} {r.user?.last_name ?? ""}</div>
                                    <div className="sp-cell-email">{r.user?.email ?? "—"}</div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="sp-item-pill">
                                  <span className="sp-item-icon" style={{ background: theme.bg }}>
                                    <TypeIcon size={11} style={{ color: theme.color }} />
                                  </span>
                                  {r.item?.name ?? "—"}
                                </span>
                              </td>
                              <td><span className={`sp-type-chip ${type}`}>{type}</span></td>
                              <td>
                                <span className={`sp-chip ${chipCls}`}>
                                  {r.status === "active" && <span className="sp-dot-pulse" />}
                                  {r.status}
                                </span>
                              </td>
                              <td>
                                <span className={`sp-pnl ${r.pnl_30d > 0 ? "pos" : r.pnl_30d < 0 ? "neg" : "zero"}`}>
                                  {r.pnl_30d > 0 ? "+" : ""}{fmt$(r.pnl_30d)}
                                </span>
                              </td>
                              <td><span className="sp-cell-date">{fmtDate(r.started_at)}</span></td>
                              <td><span className="sp-cell-date">{fmtDate(r.renews_at)}</span></td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div className="sp-row-actions">
                                  {r.status === "pending" && (
                                    <button
                                      className="sp-approve-btn"
                                      title="Approve — activate this purchase"
                                      disabled={actionBusy}
                                      onClick={() => handleStatusChange(r, "active")}
                                    >
                                      <Check size={13} />
                                    </button>
                                  )}
                                  <select
                                    className="sp-status-select"
                                    value={r.status}
                                    disabled={actionBusy}
                                    onChange={(e) => handleStatusChange(r, e.target.value)}
                                  >
                                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                                  </select>
                                  <button className="sp-icon-btn" title="Delete purchase" onClick={() => setDeleteRow(r)}>
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                    }
                  </tbody>
                </table>
              </div>

              {!rowsLoading && totalPages > 1 && (
                <div className="sp-pagination">
                  <span className="sp-pagination-info">
                    Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
                  </span>
                  <div className="sp-pagination-btns">
                    <button className="sp-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                      <ChevronLeft size={13} />
                    </button>
                    {(() => {
                      const pages = [];
                      const totalBtns = Math.min(totalPages, 7);
                      let start = Math.max(1, page - 3);
                      let end   = Math.min(totalPages, start + totalBtns - 1);
                      if (end - start < totalBtns - 1) start = Math.max(1, end - totalBtns + 1);
                      for (let p = start; p <= end; p++) {
                        pages.push(
                          <button key={p} className={`sp-page-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>
                            {p}
                          </button>
                        );
                      }
                      return pages;
                    })()}
                    <button className="sp-page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      <ChevronRight size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>

          </main>
        </div>
      </div>
    </>
  );
}